import { ForwardedRef, forwardRef, useMemo } from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import { useMergeRefs } from '@xemplo/ref-utils';

import { TextArea } from './text-area';
import { TextAreaRhfProps } from './text-area.types';

type FieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  fieldState: ControllerFieldState;
};

/**
 * TextAreaRhf is a React component that integrates the TextArea with React Hook Form.
 * It uses the Controller component to manage the form state and validation.
 *
 * @param props - The properties for the TextAreaRhf component.
 * @returns A TextArea component wrapped with React Hook Form's Controller.
 */
export const TextAreaRhf = forwardRef(
  (props: TextAreaRhfProps, ref?: ForwardedRef<HTMLTextAreaElement>) => {
    const { name, rules } = props;
    const { control } = useFormContext();
    const renderField = useMemo(() => fieldFactory({ ...props }, ref), [props, ref]);

    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        shouldUnregister={rules?.shouldUnregister}
        render={renderField}
      />
    );
  },
);

/**
 * In order to be able to use the `useMergeRefs`, had to extract the component render.
 * This should ensure correctness of the react-hook-form integration.
 **/
const fieldFactory = (
  props: TextAreaRhfProps,
  ref?: ForwardedRef<HTMLTextAreaElement>,
) => {
  return ({ field, fieldState }: FieldProps) => {
    const mergedRefs = useMergeRefs([field.ref, ref]);
    return (
      <TextArea
        {...props}
        ref={mergedRefs}
        onChange={(e) => {
          field.onChange(e);
          props.onChange?.(e);
        }}
        value={field.value ?? ''}
        errorMessage={fieldState.error?.message?.toString()}
      />
    );
  };
};
